import { useEffect, useState } from 'react';

import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import { MAX_SIZE_MB } from '@/config';
import UploadIcon from '@/icons/upload-hover.svg';
import { cn } from '@/utils';

import Spinner from '../Spinner';
import ErrorModal from './ErrorModal';
import { ACCEPTED_MIME_TYPES_BY_EXTENSIONS, MAX_FILES } from './utils';

export const ERRORS = {
	'file-too-large': 'maxSize',
	'file-invalid-type': 'unsupportedFormat'
};

const sizeClasses = {
	md: 'h-[366px]',
	sm: 'py-6',
	xs: 'py-4'
};

function UploadPDF({ onDrop, loading, showIcon = true, size = 'md' }) {
	const { t } = useTranslation();
	const [errorCode, setErrorCode] = useState(null);

	const { getRootProps, getInputProps, fileRejections } = useDropzone({
		maxFiles: MAX_FILES,
		accept: ACCEPTED_MIME_TYPES_BY_EXTENSIONS,
		maxSize: MAX_SIZE_MB * 1024 * 1024,
		async onDrop([file]) {
			onDrop(file);
		}
	});

	useEffect(() => {
		if (fileRejections.length) {
			setErrorCode(ERRORS[fileRejections[0].errors[0].code]);
		}
	}, [fileRejections]);

	return (
		<div className="relative flex w-full max-w-[1280px] items-center justify-center rounded-[36px] bg-white/50 p-4">
			{loading && (
				<div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
					<Spinner size={60} />
				</div>
			)}
			<div
				className={cn(
					'group relative flex w-full flex-col items-center justify-center rounded-3xl border border-dashed border-grey-200 bg-white px-8 hover:border-red',
					sizeClasses[size]
				)}
				{...getRootProps()}
			>
				<input
					aria-label={t('common.file')}
					{...getInputProps()}
					multiple={MAX_FILES > 1}
				/>
				{showIcon && (
					<UploadIcon width="116" height="116" className="mb-4" />
				)}

				<div className="flex flex-col items-center gap-2">
					<span
						className={cn(
							'text-center text-[32px] leading-10 text-red',
							['xs', 'sm'].includes(size) && 'text-xl'
						)}
					>
						{t('home.uploadPDF.file')}
					</span>
					<span className="text-sm font-bold leading-6 text-black">
						{t('home.uploadPDF.or')}
					</span>
					<Button className="mb-3">
						{t('common.uploadPdfToConvert')}
					</Button>
				</div>
				<span className="text-center text-xs leading-[20px] text-grey-500">
					{t('home.uploadPDF.description', { maxSize: MAX_SIZE_MB })}
				</span>
			</div>
			<ErrorModal
				open={!!errorCode}
				errorCode={errorCode}
				onClose={() => setErrorCode(null)}
			/>
		</div>
	);
}

export default UploadPDF;
